import { useCallback, useEffect, useState } from 'react';
import { fetchCampusDestinations } from 'api/campus';
import {
  buildCampusDestinations,
  CampusDestinations
} from 'utils/destinationAvailability';

// Last destinations per source facility, shown at once on reopen while fresh ones load.
const destinationCache = new Map<string, CampusDestinations>();

const EMPTY: CampusDestinations = {
  facilities: [],
  dockDoors: [],
  yardSpots: [],
  facilityZones: []
};

export interface UseCampusDestinationsResult extends CampusDestinations {
  loading: boolean;
  error: string | null;
  isMultiFacility: boolean;
  reload: () => void;
}

/**
 * Every move destination in the campus of the trailer's facility, locks applied and
 * tagged by facility. Loads while `enabled`, on source change, and on `reload`.
 */
export function useCampusDestinations(
  sourceFacilityId: string | number | null | undefined,
  enabled: boolean = true
): UseCampusDestinationsResult {
  const source =
    sourceFacilityId !== null && sourceFacilityId !== undefined
      ? String(sourceFacilityId)
      : '';
  const [destinations, setDestinations] = useState<CampusDestinations>(
    () => destinationCache.get(source) || EMPTY
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState<number>(0);

  useEffect(() => {
    if (!enabled || !source) return;

    let cancelled = false;
    const cached = destinationCache.get(source);
    if (cached) setDestinations(cached);
    // Only show a loading state when there is nothing to show yet
    setLoading(!cached);
    setError(null);

    fetchCampusDestinations(source)
      .then(({ facilities, locks }) => {
        const built = buildCampusDestinations(facilities, locks);
        destinationCache.set(source, built);
        if (!cancelled) setDestinations(built);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load campus destinations:', err);
          setError(err.message || 'Failed to load available locations');
          // A failed refresh keeps what is already on screen
          if (!destinationCache.has(source)) setDestinations(EMPTY);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [source, enabled, reloadKey]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  return {
    ...destinations,
    loading,
    error,
    isMultiFacility: destinations.facilities.length > 1,
    reload
  };
}
